import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import XIcon from './icons/XIcon';
import ZoomInIcon from './icons/ZoomInIcon';
import ZoomOutIcon from './icons/ZoomOutIcon';

const PatientPortraitViewer: React.FC<{ imageUrl: string; onClose: () => void }> = ({ imageUrl, onClose }) => {
    const [scale, setScale] = useState(1);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'auto';
        };
    }, [onClose]);

    const handleZoom = (direction: 'in' | 'out') => {
        const factor = 1.3;
        const newScale = direction === 'in' ? scale * factor : scale / factor;
        const clampedScale = Math.max(1, Math.min(newScale, 5));
        setScale(clampedScale);
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-lg"
            onClick={onClose}
        >
            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-auto h-auto max-w-[90vw] max-h-[90vh] overflow-hidden rounded-xl flex items-center justify-center"
                onClick={e => e.stopPropagation()}
                ref={containerRef}
            >
                <motion.img
                    src={imageUrl}
                    alt="Patient Showcase Portrait"
                    className="block max-w-full max-h-full object-contain"
                    style={{
                        cursor: scale > 1 ? 'grab' : 'auto',
                        aspectRatio: '3 / 2',
                    }}
                    animate={{ scale }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    drag={scale > 1}
                    dragConstraints={containerRef}
                    dragElastic={0.1}
                    whileDrag={{ cursor: 'grabbing' }}
                />
            </motion.div>
            
            <button
                onClick={onClose}
                className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-zinc-800/80 text-white flex items-center justify-center border border-white/20 hover:bg-zinc-700 transition-colors"
                aria-label="Close viewer"
            >
                <XIcon className="w-6 h-6" />
            </button>
            
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 p-1.5 rounded-full bg-zinc-900/80 backdrop-blur-md border border-white/10 flex items-center gap-2 shadow-lg">
                <button onClick={() => handleZoom('out')} className="p-2 rounded-full hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" disabled={scale <= 1}>
                    <ZoomOutIcon className="w-5 h-5 text-white" />
                </button>
                <button onClick={() => handleZoom('in')} className="p-2 rounded-full hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" disabled={scale >= 5}>
                    <ZoomInIcon className="w-5 h-5 text-white" />
                </button>
            </div>
        </motion.div>
    );
};

export default PatientPortraitViewer;
